import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ofertasService from '../helpers/ofertasService';

// Hook ultra optimizado que maximiza el rendimiento
export const useOfertasUltraOptimized = (filters = {}) => {
    const [ofertas, setOfertas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const fetchTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const lastFiltersRef = useRef('');

    // Memoizar la limpieza de filtros para evitar recálculos
    const cleanFilters = useMemo(() => {
        const clean = {};
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                clean[key] = value;
            }
        });
        return clean;
    }, [filters.tipo, filters.estado, filters.marca, filters.search]);

    // Memoizar el string de filtros para comparación
    const filtersString = useMemo(() => JSON.stringify(cleanFilters), [cleanFilters]);

    const fetchOfertas = useCallback(async (filtersToUse = cleanFilters) => {
        // Evitar múltiples fetch simultáneos
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        // Solo hacer fetch si los filtros realmente cambiaron
        const currentFiltersString = JSON.stringify(filtersToUse);
        if (currentFiltersString === lastFiltersRef.current && hasFetched) {
            return;
        }

        // Debounce para evitar fetch excesivos
        fetchTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;

            try {
                setLoading(true);
                setError(null);

                console.log('Fetching ofertas with clean filters:', filtersToUse);

                const data = await ofertasService.getOfertas(filtersToUse);

                if (isMountedRef.current) {
                    console.log('Ofertas fetched successfully:', data);
                    setOfertas(Array.isArray(data) ? data : []);
                    setHasFetched(true);
                    lastFiltersRef.current = currentFiltersString;
                }
            } catch (err) {
                console.error('Error fetching ofertas:', err);
                if (isMountedRef.current) {
                    setError(err.message || 'Error al cargar las ofertas');
                    setOfertas([]);
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        }, 200); // Debounce reducido a 200ms
    }, [cleanFilters, hasFetched]);

    useEffect(() => {
        isMountedRef.current = true;

        // Solo hacer fetch inicial si no se ha hecho antes
        if (!hasFetched) {
            fetchOfertas();
        }

        return () => {
            isMountedRef.current = false;
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, []); // Solo se ejecuta una vez al montar

    // Efecto separado para cambios en filtros (con debounce)
    useEffect(() => {
        if (hasFetched && filtersString !== lastFiltersRef.current) {
            fetchOfertas();
        }
    }, [filtersString, fetchOfertas, hasFetched]);

    const createOferta = useCallback(async (ofertaData) => {
        try {
            const validation = ofertasService.validateOfertaData(ofertaData);
            if (!validation.isValid) {
                throw new Error(validation.errors.join(', '));
            }

            const newOferta = await ofertasService.createOferta(ofertaData);
            setOfertas(prev => [...prev, newOferta]);
            return newOferta;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const updateOferta = useCallback(async (ofertaId, ofertaData) => {
        try {
            const validation = ofertasService.validateOfertaData(ofertaData);
            if (!validation.isValid) {
                throw new Error(validation.errors.join(', '));
            }

            const updatedOferta = await ofertasService.updateOferta(ofertaId, ofertaData);
            setOfertas(prev => prev.map(o => o._id === ofertaId ? updatedOferta : o));
            return updatedOferta;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteOferta = useCallback(async (ofertaId) => {
        try {
            await ofertasService.deleteOferta(ofertaId);
            setOfertas(prev => prev.filter(o => o._id !== ofertaId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const changeEstado = useCallback(async (ofertaId, nuevoEstado) => {
        try {
            const updatedOferta = await ofertasService.changeEstado(ofertaId, nuevoEstado);
            setOfertas(prev => prev.map(o => o._id === ofertaId ? updatedOferta : o));
            return updatedOferta;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const refreshOfertas = useCallback(async () => {
        setHasFetched(false);
        lastFiltersRef.current = '';
        await fetchOfertas();
    }, [fetchOfertas]);

    return {
        ofertas,
        loading,
        error,
        createOferta,
        updateOferta,
        deleteOferta,
        changeEstado,
        refreshOfertas
    };
};



